import { yupResolver } from '@hookform/resolvers/yup';
import { Button, HfCombobox, HfToggleButton } from '@myosh/odin-components';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import i18next from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { type FieldValues, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as yup from 'yup';
import { useAuth } from '../context/auth.context';
import { UserActionTypes, useUser } from '../context/user.context';
import { decodeIdToken } from '../helpers/decode-id-token';
import { type SchemaOption, schemasQueryOptions } from '../helpers/get-schemas';
import { ControlledUserPicker } from './controlled-user-picker.component';
import Footer from './footer.component';
import type { LoginResult } from './login.component';
import Sidebar from './sidebar.component';

const superUserFormSchema = yup.object().shape({
  schema: yup.object().required(() => i18next.t('schema-required')),
});

const standardFormSchema = yup.object().shape({
  schema: yup.object().required(() => i18next.t('company-required')),
});

export interface LoginSchemaProps {
  idToken: string;
  onSchemaSelected: (schema: string) => void;
}

function SchemaRedirect({ schema, onSchemaSelected }: { schema: string; onSchemaSelected: (schema: string) => void }) {
  const { dispatch } = useUser();
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only redirect; the schema is fixed by this point
  useEffect(() => {
    setTimeout(() => onSchemaSelected(schema)); // allows state to persist before redirecting (MYOSH-8225)
    dispatch({ type: UserActionTypes.UpdateCurrentSchema, payload: schema });
    dispatch({ type: UserActionTypes.UpdateLoginStatus, payload: 'done' });
  }, []);
  return null;
}

interface SchemaPickerFormProps {
  idToken: string;
  isSuperUser: boolean;
  schemas: SchemaOption[];
  isLoading?: boolean;
  onSchemaSelected: (schema: string) => void;
}

function SchemaPickerForm({ idToken, isSuperUser, schemas, isLoading, onSchemaSelected }: SchemaPickerFormProps) {
  const { t } = useTranslation();
  const { dispatch } = useUser();
  const { control, handleSubmit, clearErrors } = useForm<FieldValues>({
    resolver: yupResolver(isSuperUser ? superUserFormSchema : standardFormSchema) as never,
    defaultValues: { toggle: false },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const controlUserEnabled = !!useWatch({ control, name: 'toggle' });
  const selectedSchema = useWatch({ control, name: 'schema' }) as SchemaOption | undefined;
  const [controlledUser, setControlledUser] = useState<string | null>(null);
  const [userError, setUserError] = useState<string>();

  const onSubmit = (data: FieldValues) => {
    if (isSuperUser && controlUserEnabled) {
      if (!controlledUser) {
        setUserError(t('control-user-required'));
        return;
      }
      dispatch({ type: UserActionTypes.UpdateControlledUsername, payload: controlledUser });
    }
    setUserError(undefined);

    dispatch({ type: UserActionTypes.UpdateCurrentSchema, payload: data.schema.value });
    setTimeout(() => onSchemaSelected(data.schema.value)); // MYOSH-8225
    dispatch({ type: UserActionTypes.UpdateLoginStatus, payload: 'done' });
  };

  return (
    <form data-testid="myl-select-schema-form" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <HfCombobox
        control={control}
        name="schema"
        items={schemas}
        getKey={(schema) => schema.value}
        getLabel={(schema) => schema.text}
        placeholder={t(isSuperUser ? 'select-schema' : 'select-company')}
        isLoading={isLoading}
        onValueChange={() => setControlledUser(null)}
      />
      {isSuperUser && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <HfToggleButton
              control={control}
              inputId="control-user-toggle"
              name="toggle"
              checked={false}
              onChange={() => {
                clearErrors();
                setUserError(undefined);
                setControlledUser(null);
              }}
              className="flex"
              disabled={!selectedSchema}
            />
            <label
              htmlFor="control-user-toggle"
              className={clsx('text-sm', !selectedSchema && 'text-muted-foreground')}
            >
              {t('control-user')}
            </label>
          </div>
          {controlUserEnabled && selectedSchema && (
            <ControlledUserPicker
              key={selectedSchema.value}
              idToken={idToken}
              schema={selectedSchema.value}
              value={controlledUser}
              onValueChange={(value) => {
                setControlledUser(value);
                if (value) {
                  setUserError(undefined);
                }
              }}
              error={userError}
            />
          )}
        </div>
      )}
      <Button type="primary" htmlType="submit" className="self-start">
        {t(isSuperUser ? 'select-schema' : 'select-company')}
      </Button>
    </form>
  );
}

function LoginSchemaContent({ idToken, onSchemaSelected }: LoginSchemaProps) {
  const { t } = useTranslation();
  const { dispatch, state } = useUser();
  const { logout } = useAuth();

  const { isSuperUser, shortcutSchema } = useMemo(() => decodeIdToken(idToken), [idToken]);

  const {
    data: fetchedSchemas,
    isPending: isLoadingSchemas,
    isError: schemasError,
  } = useQuery({
    ...schemasQueryOptions(idToken),
    enabled: Boolean(idToken && !shortcutSchema),
    select: (data) =>
      (data.result ?? [])
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((s) => ({ text: isSuperUser ? `${s.name} (${s.code})` : s.name, value: s.code })),
  });

  useEffect(() => {
    if (fetchedSchemas) {
      dispatch({ type: UserActionTypes.UpdateSchemas, payload: fetchedSchemas.map((s) => s.value).join(',') });
    }
  }, [fetchedSchemas, dispatch]);

  useEffect(() => {
    if (schemasError) {
      dispatch({ type: UserActionTypes.UpdateLoginStatus, payload: 'login' });
      logout();
    }
  }, [schemasError, dispatch, logout]);

  const autoRedirectSchema = shortcutSchema ?? (fetchedSchemas?.length === 1 ? fetchedSchemas[0].value : undefined);

  return (
    <div className="myl-login-layout flex h-full min-h-screen w-full flex-col sm:flex-row">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col bg-background">
        <div className="flex max-w-sm flex-1 flex-col justify-center px-8 pb-12 md:ml-24 md:px-0">
          <h2 className="mt-8 mb-6 font-bold text-4xl sm:mt-12">
            {t('welcome-user', { name: state.user?.preferred_username ?? '' })}
          </h2>
          <p className="mb-4 sm:text-xl md:text-2xl">
            {t(isSuperUser ? 'select-schema-to-continue' : 'select-company-to-continue')}
          </p>
          {autoRedirectSchema ? (
            <SchemaRedirect schema={autoRedirectSchema} onSchemaSelected={onSchemaSelected} />
          ) : (
            <SchemaPickerForm
              idToken={idToken}
              isSuperUser={isSuperUser}
              schemas={fetchedSchemas ?? []}
              isLoading={isLoadingSchemas}
              onSchemaSelected={onSchemaSelected}
            />
          )}
        </div>
        <Footer />
      </div>
    </div>
  );
}

const queryClient = new QueryClient();

export default function LoginSchema(props: LoginSchemaProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <LoginSchemaContent {...props} />
    </QueryClientProvider>
  );
}

// A convenience hook that provides the schema selection callback
export const useLoginSchema = (redirectPath?: string, onLoginSuccess?: (result: LoginResult) => void) => {
  const navigate = useNavigate();
  const { state } = useUser();
  const tokens = state.tokens;

  const onSchemaSelected = async (schema: string) => {
    // redirect after success
    if (redirectPath) {
      const redirectQueryParams = localStorage.getItem('redirectQueryParams');

      // add query params to redirect
      if (redirectQueryParams) {
        const queryParams = new URLSearchParams(JSON.parse(redirectQueryParams)).toString();

        return navigate(
          {
            pathname: redirectPath,
            search: `?${queryParams}`,
          },
          { replace: true }
        );
      }
      return navigate(
        {
          pathname: redirectPath,
        },
        { replace: true }
      );
    }
    // data for manual token storage and event trigger for a successful login
    if (onLoginSuccess) {
      onLoginSuccess({
        accessToken: tokens?.access_token || '',
        idToken: tokens?.id_token || '',
        refreshToken: tokens?.refresh_token || '',
        selectedSchema: schema,
      });
    }
  };

  return { onSchemaSelected };
};
